import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import LoginHeader from "../common/LoginHeader";
import PhoneFormInput from "../common/PhoneInput";
import PrimaryButton from "../common/PrimaryButton";
import Loader from "../common/Loader";
import axios from "../../utils/axios";
import toast from "../../utils/toast";
import Selector from "../common/Selector";
import { ReactComponent as DownArrow } from "../../Assets/Icons/greyDownArrow.svg";
import "../User/ComingSoon/comingsoon.scss";

const PENDING_FUNDRAISER_MESSAGE =
  "Your account is pending admin approval. Please wait for approval.";
const MAIN_SITE_URL = "https://www.swoodle.com.au";

const FundraiserComingSoon = () => {
  const location = useLocation();
  const [phone, setPhone] = useState("");
  const [describeYourself, setDescribeYourself] = useState("");
  const [selectedDescribeOption, setSelectedDescribeOption] = useState("");
  const [usedDigitalPlatform, setUsedDigitalPlatform] = useState("");
  const [heardAboutUs, setHeardAboutUs] = useState("");
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [hasSurveyChanges, setHasSurveyChanges] = useState(false);
  const [inlineMessage, setInlineMessage] = useState("");

  useEffect(() => {
    const blockedStatus = String(
      sessionStorage.getItem("fundraiser_block_status") || "",
    ).toLowerCase();
    const locationMessage = String(location.state?.message || "");

    if (locationMessage.toLowerCase().includes("pending")) {
      setInlineMessage(locationMessage);
      return;
    }

    if (
      (blockedStatus === "pending" || blockedStatus === "in_progress") &&
      sessionStorage.getItem("showAccountPendingModal") === "true"
    ) {
      setInlineMessage(PENDING_FUNDRAISER_MESSAGE);
    }
  }, [location.state]);

  useEffect(() => {
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.history.pushState({ fundraiserComingSoon: true }, "", currentUrl);

    const handlePopState = () => {
      sessionStorage.removeItem("fundraiser_waitlist_token");
      window.location.replace(MAIN_SITE_URL);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const submitDisabled = useMemo(
    () => !hasSurveyChanges || loading,
    [hasSurveyChanges, loading],
  );

  const markSurveyDirty = (setter) => (valueOrEvent) => {
    setHasSurveyChanges(true);

    if (valueOrEvent?.target) {
      setter(valueOrEvent.target.value);
      return;
    }

    setter(valueOrEvent);
  };

  const handleNotifyMe = async (event) => {
    if (event) event.preventDefault();

    if (!phone || phone.trim().length <= 4) {
      toast.error("Please enter a valid mobile number.");
      return;
    }

    const waitlistToken = sessionStorage.getItem("fundraiser_waitlist_token");

    setLoading(true);
    try {
      const response = await axios.post("waitlist/fundraiser", {
        phone: phone.trim(),
        token: waitlistToken || undefined,
        verificationCode: location.state?.verificationCode,
      });

      if (response.data?.success) {
        toast.success("Thanks! We've saved your mobile number.");
        setStep(2);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error(error.message || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (submitDisabled) return;

    const waitlistToken = sessionStorage.getItem("fundraiser_waitlist_token");

    setLoading(true);
    try {
      const response = await axios.post("waitlist/fundraiser", {
        phone: phone.trim(),
        token: waitlistToken || undefined,
        verificationCode: location.state?.verificationCode,
        surveyAnswers: {
          describeYourself: describeYourself.trim(),
          usedDigitalPlatform: usedDigitalPlatform.trim(),
          heardAboutUs: heardAboutUs,
          additionalFeedback: additionalFeedback.trim(),
        },
      });

      if (response.data?.success) {
        toast.success("Thank you! Your feedback has been saved.");
        sessionStorage.removeItem("fundraiser_waitlist_token");
        window.location.href = MAIN_SITE_URL;
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error(error.message || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReturnToMainLogin = () => {
    sessionStorage.removeItem("fundraiser_waitlist_token");
    window.location.href = MAIN_SITE_URL;
  };

  return (
    <div className="coming-soon-page w-full min-h-screen flex flex-col">
      {loading ? <Loader /> : null}
      <div className="w-full px-4 md:px-6 pt-2 md:pt-3">
        <div className="w-full max-w-[1920px] mx-auto">
          <LoginHeader />
        </div>
      </div>

      <main className="coming-soon-main fundraiser-coming-soon-main flex-grow py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-[760px] mx-auto">
          {step === 1 && inlineMessage ? (
            <p className="text-center text-[#EF4444] font-poppins font-[600] text-[14px] sm:text-[16px] mb-10">
              {inlineMessage}
            </p>
          ) : null}

          {step === 1 ? (
            <form
              onSubmit={handleNotifyMe}
              className="w-full max-w-[620px] mx-auto flex flex-col gap-6"
            >
              <div className="text-center flex flex-col gap-4 mb-4">
                <h1 className="coming-soon-title text-[#00ADEF] font-poppins font-extrabold tracking-tight">
                  We&apos;re launching soon!
                </h1>
                <p className="coming-soon-subtitle text-gray-500 text-base sm:text-lg leading-relaxed font-poppins font-light">
                  Thanks for checking out Swoodle. We&apos;re putting the
                  finishing touches on the platform. Leave your mobile number
                  below and we&apos;ll send you a message as soon as we go live.
                </p>
              </div>

              <div className="form-group flex flex-col gap-2">
                <label
                  htmlFor="phone"
                  className="text-sm font-semibold text-[#08264a] font-poppins text-left"
                >
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <PhoneFormInput
                  name="phone"
                  value={phone}
                  setFieldValue={(_name, value) => setPhone(value)}
                  placeholder="e.g. +61 400 000 000"
                  containerClass="coming-soon-phone-input"
                />
              </div>

              <button
                type="submit"
                className="notify-button w-full py-4 rounded-xl text-white font-poppins font-bold text-base transition-all"
              >
                Notify Me
              </button>
            </form>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-6 max-w-[760px] mx-auto"
            >
              <div className="text-center">
                <h2 className="coming-soon-title text-[28px] text-[#00ADEF] font-poppins font-extrabold text-center">
                  We&apos;re launching soon!
                </h2>
              </div>

              <div className="rounded-[24px] border border-[#d8e2ec] shadow-[0_8px_24px_rgba(0,0,0,0.08)] p-6 sm:p-8 flex flex-col gap-6">
                <div>
                  <p className="text-[#08264a] text-[24px] font-poppins font-[700]">
                    Help shape Swoodle (Quick Survey){" "}
                    <span className="text-[#38BDF8]">(Optional)</span>
                  </p>
                  <p className="text-[#9CA3AF] text-[15px] sm:text-[16px] font-poppins mt-1 leading-relaxed">
                    Your feedback will help us build a better platform and
                    deliver the features that matter most to you.
                  </p>
                </div>

                <div className="form-group flex flex-col gap-2">
                  <label className="text-[16px] sm:text-[18px] font-[700] text-[#111827] font-poppins">
                    1. What best describes you?
                  </label>
                  <Selector
                    value={selectedDescribeOption}
                    onChange={(val) => {
                      setSelectedDescribeOption(val);
                      if (val !== "Other") {
                        markSurveyDirty(setDescribeYourself)(val);
                      } else {
                        markSurveyDirty(setDescribeYourself)("");
                      }
                    }}
                    placeholder="Select an option"
                    options={[
                      { label: "Sporting Clubs", value: "Sporting Club" },
                      { label: "Schools", value: "School" },
                      { label: "Community Organisations", value: "Community Organisation" },
                      { label: "Other", value: "Other" },
                    ]}
                    suffixIcon={<DownArrow />}
                    selectClassName="w-full !font-poppins"
                    divClassName="w-full"
                  />

                  {selectedDescribeOption === "Other" && (
                    <textarea
                      rows="2"
                      placeholder="Please describe your organisation..."
                      value={describeYourself}
                      onChange={markSurveyDirty(setDescribeYourself)}
                      className="survey-textarea w-full px-4 py-3 border border-[#D9D9D9] rounded-xl font-poppins resize-none focus:outline-none focus:border-[#007aff]"
                    />
                  )}
                </div>

                <div className="form-group flex flex-col gap-2">
                  <label className="text-[16px] sm:text-[18px] font-[700] text-[#111827] font-poppins">
                    2. Have you used a Digital platform to Fundraise before?
                  </label>
                  <textarea
                    rows="2"
                    placeholder="If 'yes' which one?"
                    value={usedDigitalPlatform}
                    onChange={markSurveyDirty(setUsedDigitalPlatform)}
                    className="survey-textarea w-full px-4 py-3 border border-[#D9D9D9] rounded-xl font-poppins resize-none focus:outline-none focus:border-[#007aff]"
                  />
                </div>

                <div className="form-group flex flex-col gap-2">
                  <label className="text-[16px] sm:text-[18px] font-[700] text-[#111827] font-poppins">
                    3. How did you hear about us?
                  </label>
                  <Selector
                    value={heardAboutUs}
                    onChange={markSurveyDirty(setHeardAboutUs)}
                    placeholder="Select an option"
                    options={[
                      { label: "Social Media", value: "Social Media" },
                      { label: "Friend or Family", value: "Friend or Family" },
                      { label: "Fundraiser Invitation", value: "Fundraiser Invitation" },
                      { label: "Online Search", value: "Online Search" },
                      { label: "Other", value: "Other" },
                    ]}
                    suffixIcon={<DownArrow />}
                    selectClassName="w-full !font-poppins"
                    divClassName="w-full"
                  />
                </div>

                <div className="form-group flex flex-col gap-2">
                  <label className="text-[16px] sm:text-[18px] font-[700] text-[#111827] font-poppins">
                    4. Is there anything else you&apos;d like to see on the
                    platform?
                  </label>
                  <textarea
                    rows="2"
                    placeholder="Describe any other features or feedback..."
                    value={additionalFeedback}
                    onChange={markSurveyDirty(setAdditionalFeedback)}
                    className="survey-textarea w-full px-4 py-3 border border-[#D9D9D9] rounded-xl font-poppins resize-none focus:outline-none focus:border-[#007aff]"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-between w-full gap-4 mt-2">
                <PrimaryButton
                  type="button"
                  label="Return to Main Login screen"
                  onClick={handleReturnToMainLogin}
                  className="flex-1 h-[52px] rounded-[8px]"
                />
                <PrimaryButton
                  type="submit"
                  label="Submit"
                  disabled={submitDisabled}
                  loading={loading}
                  className="flex-1 h-[52px] rounded-[8px]"
                />
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default FundraiserComingSoon;
